import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageCheck, ArrowLeft, Search, FileSpreadsheet, Printer, DollarSign, Package } from 'lucide-react';
import Layout from '../../../components/Layout';
import { inventoryReportService, GRNReportItemDTO } from '../../../services/inventoryReportService';
import { exportToCsv, printReport } from '../../../utils/exportUtils';
import './InventoryReportCommon.css';

const fmt    = (n: number) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtQty = (n: number) => new Intl.NumberFormat().format(n);
const fmtDate = (d: string) => new Date(d).toLocaleDateString();
const today   = () => new Date().toISOString().split('T')[0];
const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; };

const statusBadge = (s: string) => {
  const map: Record<string, string> = {
    Draft: 'ims-badge-draft', Received: 'ims-badge-confirmed',
    Verified: 'ims-badge-approved', Completed: 'ims-badge-completed',
  };
  return <span className={`ims-badge ${map[s] ?? 'ims-badge-draft'}`}>{s}</span>;
};

const GRNReportPage: React.FC = () => {
  const navigate  = useNavigate();
  const [startDate,   setStartDate]   = useState(monthAgo());
  const [endDate,     setEndDate]     = useState(today());
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [items,       setItems]       = useState<GRNReportItemDTO[]>([]);

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await inventoryReportService.getGRNReport({ startDate, endDate });
      const d: GRNReportItemDTO[] = (res.data as any)?.data ?? res.data ?? [];
      setItems(d);
    } catch { setItems([]); }
    finally { setSearching(false); }
  };

  const handleExcel = () => {
    exportToCsv(
      `grn-report-${startDate}_${endDate}.csv`,
      ['GRN #', 'Date', 'Supplier', 'Warehouse', 'Type', 'Status', 'Approval', 'Items', 'Total Qty', 'Total Value', 'PO #'],
      items.map(i => [
        i.grnNumber, fmtDate(i.grnDate), i.supplierName, i.warehouseName,
        i.grnType, i.status, i.approvalStatus ?? '', i.totalItems,
        i.totalQuantityReceived, i.totalValue, i.purchaseOrderNumber ?? '',
      ]),
    );
  };

  const totalValue = items.reduce((s, i) => s + i.totalValue, 0);
  const totalQty   = items.reduce((s, i) => s + i.totalQuantityReceived, 0);

  return (
    <Layout>
      <div className="irp-page">

        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title"><PackageCheck size={22} strokeWidth={1.8} /> GRN Report</h1>
            <p className="irp-hero-sub">All Goods Received Notes with supplier, warehouse, and value details</p>
          </div>
          <div className="irp-hero-actions">
            {hasSearched && items.length > 0 && (
              <div className="irp-export-group">
                <button className="irp-export-btn irp-export-excel" onClick={handleExcel}><FileSpreadsheet size={13} /> Export Excel</button>
                <button className="irp-export-btn irp-export-pdf" onClick={printReport}><Printer size={13} /> Print / PDF</button>
              </div>
            )}
            <button className="irp-back-btn" onClick={() => navigate('/reports/inventory')}><ArrowLeft size={14} /> Back</button>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title"><Search size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />Date Range Filter</span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="irp-filter-bar">
              <div className="irp-filter-group">
                <span className="irp-filter-label">From Date</span>
                <input type="date" className="ims-form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="irp-filter-group">
                <span className="irp-filter-label">To Date</span>
                <input type="date" className="ims-form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
              <button className="ims-btn ims-btn-primary" onClick={handleSearch} disabled={searching} style={{ alignSelf: 'flex-end' }}>
                {searching ? <><span className="irp-spinner" /> Searching…</> : <><Search size={14} /> Search</>}
              </button>
            </div>
          </div>
        </div>

        {!hasSearched && (
          <div className="irp-prompt"><PackageCheck size={48} strokeWidth={1} /><p>Set a date range and click <strong>Search</strong>.</p></div>
        )}

        {hasSearched && !searching && (
          <>
            <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-total"><PackageCheck size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{items.length}</span>
                  <span className="ims-stat-label">Total GRNs</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-in"><Package size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{fmtQty(Math.round(totalQty))}</span>
                  <span className="ims-stat-label">Total Qty Received</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-value"><DollarSign size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totalValue)}</span>
                  <span className="ims-stat-label">Total Value Received</span>
                </div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="ims-card"><div className="irp-empty">No GRNs found for the selected period.</div></div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <PackageCheck size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    GRN Records ({items.length})
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="irp-table">
                    <thead>
                      <tr>
                        <th>GRN #</th>
                        <th>Date</th>
                        <th>Supplier</th>
                        <th>Warehouse</th>
                        <th>Type</th>
                        <th>PO #</th>
                        <th className="num">Items</th>
                        <th className="num">Qty Received</th>
                        <th className="num">Total Value</th>
                        <th>Status</th>
                        <th>Approval</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.grnId}>
                          <td><span className="irp-doc-num">{item.grnNumber}</span></td>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(item.grnDate)}</td>
                          <td style={{ fontWeight: 500 }}>{item.supplierName}</td>
                          <td>{item.warehouseName}</td>
                          <td>
                            <span className={`ims-badge ${item.grnType === 'WithPO' ? 'ims-badge-confirmed' : 'ims-badge-draft'}`}>
                              {item.grnType}
                            </span>
                          </td>
                          <td>{item.purchaseOrderNumber
                            ? <span className="irp-sku">{item.purchaseOrderNumber}</span>
                            : '—'}
                          </td>
                          <td className="num">{item.totalItems}</td>
                          <td className="num">{fmtQty(Math.round(item.totalQuantityReceived))}</td>
                          <td className="num irp-value">{fmt(item.totalValue)}</td>
                          <td>{statusBadge(item.status)}</td>
                          <td>
                            {item.approvalStatus && (
                              <span className={`ims-badge ${
                                item.approvalStatus === 'Approved' ? 'ims-badge-approved' :
                                item.approvalStatus === 'Rejected' ? 'ims-badge-rejected' :
                                'ims-badge-pending'}`}>
                                {item.approvalStatus}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={7} style={{ fontWeight: 700 }}>TOTAL</td>
                        <td className="num">{fmtQty(Math.round(totalQty))}</td>
                        <td className="num">{fmt(totalValue)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default GRNReportPage;
